import React from 'react'

import type { FloatingTreeType, Placement, ReferenceType } from '@floating-ui/react'
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDelayGroup,
  useDismiss,
  useFloating,
  useFloatingParentNodeId,
  useFloatingTree,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react'
import styled, { css, useTheme } from 'styled-components'

import { useFloatingTransitionStyles } from '@libs/typescript-utils'

// Do not import from @hooks/index.ts because it will cause a circular dependency due to listFilters.tsx "helper"
import { useIsInsideDialog } from '@hooks/useIsInsideDialog'
import { useWindowWidth } from '@hooks/useWindowWidth'

import type { PopoverProps } from '@components/ui/Popover'

import { FLOATING_TRANSITION_DURATION } from '@constants'

const TooltipContainer = styled.div<{ $interactive: boolean }>`
  ${({ theme, $interactive }) => css`
    z-index: ${theme.zIndex.dialog - 1};
    pointer-events: ${$interactive ? 'auto' : 'none'};

    ${$interactive &&
    css`
      ${TooltipPaper} {
        user-select: unset;
        max-width: unset;
        padding: 0;
      }
    `}
  `}
`

const TooltipPaper = styled.div`
  padding: 4px 8px;
  max-width: 300px;
  font-size: calc(5 / 7 * 1rem);
  font-weight: 500;
  line-height: 1.4em;
  word-wrap: break-word;
  white-space: normal;
  text-align: left;
  background-color: ${({ theme }) => theme.colors.gray[0]};
  border-radius: 4px;
  box-shadow: ${({ theme }) => theme.shadows[10]};
  color: ${({ theme }) => theme.colors.gray[180]};
  user-select: none;
`

/**
 * Helper function to check if React children contain any text content
 * Recursively traverses through React nodes to find text content
 */
function hasTextContent(children: React.ReactNode | React.PropsWithChildren['children']): boolean {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children).trim().length > 0
  }

  if (React.isValidElement<{ children: React.ReactNode }>(children)) {
    // Check if the element has text content in its children
    return hasTextContent(children.props.children)
  }

  if (Array.isArray(children)) {
    // Check if any child in the array has text content
    return children.some(child => hasTextContent(child))
  }

  // Handle React fragments
  if (children && typeof children === 'object' && 'props' in children) {
    return hasTextContent((children as { props?: { children: React.ReactNode } }).props?.children)
  }

  return false
}

// Helper function to get the z-index of the tooltip based on a possible parent floating element
function getZIndex({
  isInsideDialog,
  dialogZIndex,
  floatingTree,
  floatingParentId,
}: {
  isInsideDialog: boolean
  dialogZIndex: number
  floatingTree: Nullable<FloatingTreeType>
  floatingParentId: Nullable<string>
}) {
  let floatingZIndex = 0
  if (floatingParentId) {
    const parent = floatingTree?.nodesRef.current.find(node => node.id === floatingParentId)
    if (parent && parent.context?.elements.floating) {
      const computedStyle = getComputedStyle(parent.context.elements.floating)
      const zIndex = computedStyle.getPropertyValue('z-index')
      floatingZIndex = parseInt(zIndex)
    }
  }
  if (isInsideDialog) {
    return Math.max(dialogZIndex + 1, floatingZIndex)
  } else {
    return Math.max(dialogZIndex - 1, floatingZIndex)
  }
}

interface ChildrenProps {
  ref: ((node: ReferenceType | null) => void) & ((node: ReferenceType | null) => void)
  style?: React.CSSProperties
}

export interface TooltipProps extends Pick<PopoverProps, 'onOpenChangeCallback'> {
  /** Needs to be a ReactElement that accepts a ref */
  children: React.ReactElement<ChildrenProps>
  interactive?: boolean
  placement?: Placement
  style?: React.CSSProperties // Required for style appending HOC usage, like Fade component
  title: React.ReactNode
  withPortal?: boolean
}

/**
 * Tooltip component
 *
 * For usage with disabled buttons, follow the example below:
 * ```tsx
 *  <button
 *   {...props}
 *   disabled={visuallyDisabled ? undefined : disabled}
 *   aria-disabled={visuallyDisabled ? 'true' : undefined}
 *   // You'll want to do this for all relevant event handlers.
 *   onClick={visuallyDisabled ? undefined : props.onClick}
 *  />
 * ```
 */
export const Tooltip = React.forwardRef<ReferenceType, TooltipProps>(
  (
    {
      children,
      interactive = false,
      onOpenChangeCallback,
      placement = 'bottom',
      style,
      title,
      withPortal = false,
    }: TooltipProps,
    ref
  ) => {
    const [isOpen, setOpen] = React.useState(false)
    const isAboveBreakpoint = useWindowWidth(600)
    const floatingParentId = useFloatingParentNodeId()
    const floatingTree = useFloatingTree()
    const { refs, floatingStyles, context } = useFloating({
      middleware: [offset(isAboveBreakpoint ? 14 : 24, [isAboveBreakpoint]), flip(), shift()],
      open: isOpen,
      onOpenChange: (nextOpen, event, reason) => {
        setOpen(nextOpen)
        onOpenChangeCallback?.({
          nextOpen,
          event,
          reason,
          transitionDuration: FLOATING_TRANSITION_DURATION,
        })
      },
      placement,
      whileElementsMounted: autoUpdate,
    })

    const referenceRefs = useMergeRefs([refs.setReference, ref]) as ChildrenProps['ref']

    const { currentId, delay, isInstantPhase } = useDelayGroup(context)

    const hover = useHover(context, {
      move: false,
      delay,
      ...(interactive && { handleClose: safePolygon() }),
    })
    const focus = useFocus(context)
    const dismiss = useDismiss(context)

    // Determine role based on whether children contain text content
    const role = useRole(context, {
      // If reference element has its own label (text), use 'tooltip'
      // If reference element does not have its own label (e.g. an icon), use 'label'
      role: hasTextContent(children) ? 'tooltip' : 'label',
    })

    const { getFloatingProps, getReferenceProps } = useInteractions([hover, focus, dismiss, role])

    const instantDuration = 0
    const { isMounted, styles: transitionStyles } = useFloatingTransitionStyles(
      context,
      placement,
      isInstantPhase
        ? {
            open: instantDuration,
            close: currentId === context.floatingId ? FLOATING_TRANSITION_DURATION : instantDuration,
          }
        : FLOATING_TRANSITION_DURATION
    )

    // Pass the extra props to the trigger that is handling the floating ui logic.
    let childrenWithExtraProps = children
    // Checking isValidElement is the safe way and avoids a typescript error too.
    if (React.isValidElement(children)) {
      childrenWithExtraProps = React.cloneElement(children, {
        ref: referenceRefs,
        style,
        ...getReferenceProps(),
      })
    }

    const TooltipWrapper = withPortal ? FloatingPortal : React.Fragment
    const isInsideDialog = useIsInsideDialog(refs.domReference.current)
    const { zIndex } = useTheme()

    return (
      <>
        {childrenWithExtraProps}
        {isMounted && (
          <TooltipWrapper>
            <TooltipContainer
              $interactive={interactive}
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                zIndex: getZIndex({
                  isInsideDialog,
                  dialogZIndex: zIndex.dialog,
                  floatingTree,
                  floatingParentId: floatingParentId,
                }),
              }}
              {...getFloatingProps()}
            >
              <TooltipPaper style={transitionStyles}>{title}</TooltipPaper>
            </TooltipContainer>
          </TooltipWrapper>
        )}
      </>
    )
  }
)

Tooltip.displayName = 'Tooltip'
